import { createContext, useContext, useEffect } from 'react';
import { useLoginSession } from './SessionContext.tsx';
import React from 'react';
import { useKeyValueStore, useRawLocalState, useRcAdapter } from './GlobalState.tsx';
import { InsufficientEntitlementError, PREMIUM_ENTITLEMENT_NAME } from './utils.ts';
import { differenceInCalendarDays } from 'date-fns';
import { TIME_ZONE } from './Constants.tsx';
import { TZDate } from '@date-fns/tz';
import * as Sentry from '@sentry/capacitor';

export enum PaywallTrigger {
    ARCHIVE_ACCESS,
    USER_INITIATED_UPGRADE,
}

export type SubscriptionContextAPI = {
    // Outputs
    ensureProLevel: (trigger: PaywallTrigger) => Promise<void>;
    isPro: boolean;
    pro: () => boolean;
    paywallTrigger: PaywallTrigger | null;
    refreshProStatus: () => Promise<boolean>;
    paywallIsOpen: boolean;

    // Inputs
    onboardingSucceeded: (useCache?: boolean) => void;
    onboardingFailed: (reason: Error) => void;
    onboardingBypassed: () => void;
};

const SubscriptionContext = createContext<SubscriptionContextAPI | undefined>(undefined);

export function SubscriptionProvider({ children }: { children: React.ReactNode }) {
    const loginSession = useLoginSession();
    const rcAdapter = useRcAdapter();
    const initialLocalState = useRawLocalState();
    const keyValueStore = useKeyValueStore();

    const [lastProVerificationDate, setLastProVerificationDate] = React.useState<Date | null>(
        getInitialLastProVerificationDate,
    );
    const [isPro, setIsPro] = React.useState<boolean>(getInitialIsPro);
    const isProRef = React.useRef<boolean>(isPro);
    const [paywallTrigger, setPaywallTrigger] = React.useState<PaywallTrigger | null>(null);
    const [paywallIsOpen, setPaywallIsOpen] = React.useState<boolean>(false);

    const promiseRef = React.useRef<Promise<void> | null>(null);

    const resolveRef = React.useRef<((value: void | PromiseLike<void>) => void) | null>(null);
    const rejectRef = React.useRef<((reason?: any) => void) | null>(null);

    function getInitialLastProVerificationDate() {
        if (!initialLocalState.lastProVerificationDate) {
            return null;
        }
        try {
            return new Date(initialLocalState.lastProVerificationDate);
        } catch (e) {
            console.error('Error parsing lastProVerificationDate from global state:', e);
            Sentry.captureException(e);
            return null;
        }
    }

    useEffect(() => {
        if (loginSession.session) {
            void refreshProStatus();
        } else {
            setIsPro(false);
            isProRef.current = false;
            setLastProVerificationDate(null);
        }
    }, [loginSession.session]);

    useEffect(() => {
        if (lastProVerificationDate) {
            void keyValueStore.setValue('lastProVerificationDate', lastProVerificationDate?.toISOString());
        }
    }, [lastProVerificationDate]);

    function getInitialIsPro() {
        const lastProVerificationDate = getInitialLastProVerificationDate();
        if (!lastProVerificationDate) {
            return false;
        }
        return differenceInCalendarDays(new TZDate(new Date(), TIME_ZONE), lastProVerificationDate) < 7;
    }

    const refreshProStatus = async (useCache: boolean = false) => {
        const userId = loginSession.getUserId();
        if (!userId) {
            throw new Error(`User id missing from session ${JSON.stringify(loginSession.session)}`);
        }
        const hasPro = await rcAdapter.hasEntitlement(userId, PREMIUM_ENTITLEMENT_NAME, useCache);
        setIsPro(hasPro);
        isProRef.current = hasPro;
        if (hasPro) {
            setLastProVerificationDate(new TZDate(new Date(), TIME_ZONE));
        }
        return hasPro;
    };

    const cleanup = () => {
        console.info('SubscriptionContext: cleaning up, setting paywallIsOpen to false...');
        resolveRef.current = null;
        rejectRef.current = null;
        promiseRef.current = null;
        setPaywallTrigger(null);
        setPaywallIsOpen(false);
    };

    const onboardingSucceeded = async (useCache: boolean = true) => {
        // Use the cache when coming directly from onboarding, since this will be more updated
        // than the server when the purchase was made on this device
        const hasPro = await refreshProStatus(useCache);
        if (hasPro) {
            resolveRef.current?.();
        } else {
            rejectRef.current?.(
                new InsufficientEntitlementError('User does not have pro entitlement after onboarding'),
            );
        }
    };

    const onboardingFailed = (reason: Error) => {
        rejectRef.current?.(reason);
    };

    const onboardingBypassed = () => {
        // The user does not have pro, but they bypassed onboarding (e.g. watched a reward ad), so let them
        // proceed.
        console.info('SubscriptionContext: onboarding bypassed...');
        resolveRef.current?.();
    };

    const ensureProLevel = (trigger: PaywallTrigger) => {
        // Make sure there is still a valid session, i.e the user didn't just log out
        const sessionStillValid = loginSession.getUserId() != null;
        if (isPro && sessionStillValid) {
            return Promise.resolve();
        }
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        if (promiseRef.current) {
            return promiseRef.current;
        }
        setPaywallTrigger(trigger);
        setPaywallIsOpen(true);
        const p = new Promise<void>((resolve, reject) => {
            resolveRef.current = () => {
                try {
                    resolve();
                } finally {
                    cleanup();
                }
            };
            rejectRef.current = (e: Error) => {
                try {
                    reject(e);
                } finally {
                    cleanup();
                }
            };
        });
        promiseRef.current = p;
        return p;
    };

    const value = {
        ensureProLevel,
        isPro,
        pro: () => isProRef.current,
        paywallTrigger,
        onboardingFailed,
        onboardingSucceeded,
        onboardingBypassed,
        refreshProStatus,
        paywallIsOpen,
    };
    return <SubscriptionContext.Provider value={value}>{children}</SubscriptionContext.Provider>;
}

export function useSubscriptionContext(): SubscriptionContextAPI {
    const ctx = useContext(SubscriptionContext);
    if (!ctx) throw new Error('useProContext must be used within a ProContextProvider');
    return ctx;
}
